import React, { useEffect, useMemo, useState } from 'react';

import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';

import './LuxPlusWorkbook.css';
import OverviewMembershipMetrics from './OverviewMembershipMetrics';
import {
  LuxPlusControlContext,
  type LuxPlusControls,
  ReportButton,
  ReportInput,
  ReportSelect,
  htmlControls,
} from './WorkbookControls';
import { sourceRecordLink } from './controls';
import { getFidelityDefaultFilters } from './filterBindings';
import type {
  FidelityCadence,
  FidelityDashboard,
  FidelityFilterControl,
  FidelityFilters,
  FidelityPanel,
  FidelityRow,
  FidelityViewId,
  MetricFormat,
} from './presentationModel';
import { FIDELITY_VIEWS } from './presentationModel';

export type LuxPlusWorkbookProps = {
  board: FidelityDashboard;
  printing?: boolean;
  previewRows?: boolean;
  reportActions?: React.ReactNode;
  model: { markets: Array<string>; range: { min: string | null; max: string | null } };
  filterOptions: Array<FidelityFilterControl>;
  ready: boolean;
  problem: string | null;
  loading?: boolean;
  loadedCards?: number;
  totalCards?: number;
  streamedMetrics?: Array<{ label: string; value: number }>;
  filters: FidelityFilters;
  view: FidelityViewId;
  onChange: (change: Partial<FidelityFilters> & { view?: FidelityViewId }) => void;
  cadence: FidelityCadence;
  onCadence: (cadence: FidelityCadence) => void;
  split: 'market' | undefined;
  onSplit: (split: 'market' | undefined) => void;
  theme: 'light' | 'dark';
  onTheme?: () => void;
  layout: 'baseline' | 'workbook';
  onLayout: (layout: 'baseline' | 'workbook') => void;
  controls?: LuxPlusControls;
  nativeControls?: boolean;
  nativeFlow?: boolean;
  exportPanel: (panel: FidelityPanel) => void;
  extraControls?: React.ReactNode;
  onSourceMark?: (row: FidelityRow, keys: Array<string>) => void;
  /** "Data queried …" from the Lens response; the source table refresh time isn't available to the page. */
  queriedAt?: string | null;
};

function formatted(value: unknown, format?: MetricFormat, precision?: number): string {
  if (value === null || value === undefined || (typeof value === 'number' && !Number.isFinite(value))) {
    return 'Unknown';
  }
  if (typeof value !== 'number') {
    return String(value);
  }
  if (format === 'percent') {
    return new Intl.NumberFormat('en-AU', { style: 'percent', maximumFractionDigits: 1 }).format(value);
  }
  const n = new Intl.NumberFormat('en-AU', {
    maximumFractionDigits: precision ?? (format === 'decimal' ? 1 : 0),
  }).format(Math.abs(value));
  return `${value < 0 ? '−' : ''}${format === 'money' ? 'A$' : ''}${n}`;
}

function shortDate(value: unknown) {
  const text = String(value ?? '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${text}T00:00:00Z`),
  );
}

function compact(value: unknown, format?: MetricFormat) {
  if (typeof value !== 'number') {
    return formatted(value, format);
  }
  if (format === 'percent') {
    return `${Math.round(value * 100)}%`;
  }
  return `${value < 0 ? '−' : ''}${format === 'money' ? '$' : ''}${new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(Math.abs(value))}`;
}

function DimensionFilter({
  option,
  value,
  onChange,
}: {
  option: FidelityFilterControl;
  value: string;
  onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const searchable = option.options.length > 80;
  const matches = searchable
    ? option.options.filter((item) => draft === 'All' || item.toLowerCase().includes(draft.toLowerCase())).slice(0, 80)
    : [];
  return (
    <div className="lp-dimension-filter">
      <label>
        {option.label}
        {searchable ? (
          <>
            <ReportInput
              aria-label={option.label}
              list={`lp-options-${option.key}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onChange(draft || 'All');
                }
              }}
            />
            <datalist id={`lp-options-${option.key}`}>
              {matches.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
          </>
        ) : (
          <ReportSelect aria-label={option.label} value={value} onChange={(event) => onChange(event.target.value)}>
            {option.options.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </ReportSelect>
        )}
        {option.note && <small>{option.note}</small>}
      </label>
      {searchable && draft !== value && (
        <ReportButton onClick={() => onChange(draft || 'All')}>Apply {option.label}</ReportButton>
      )}
    </div>
  );
}

function PanelTable({
  panel,
  onMarket,
  onSourceMark,
  printing = false,
  previewRows = false,
}: {
  panel: FidelityPanel;
  printing?: boolean;
  previewRows?: boolean;
  onMarket: (market: string) => void;
  onSourceMark?: (row: FidelityRow, keys: Array<string>) => void;
}) {
  const [sort, setSort] = useState(panel.initialSort ?? { key: panel.columns[0]?.key ?? '', direction: 1 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [panel.rows, search, sort.key, sort.direction]);
  const rows = useMemo(
    () =>
      [...panel.rows].sort((a, b) => {
        const x = a[sort.key],
          y = b[sort.key];
        if (x == null) {
          return y == null ? 0 : 1;
        }
        if (y == null) {
          return -1;
        }
        return (
          (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * sort.direction
        );
      }),
    [panel.rows, sort],
  );
  const heatValues =
    panel.kind === 'heatmap'
      ? panel.rows.flatMap((row) =>
          panel.columns
            .slice(1)
            .map((c) => row[c.key])
            .filter((v): v is number => typeof v === 'number'),
        )
      : [];
  const heatMin = heatValues.length ? Math.min(...heatValues) : 0;
  const heatMax = heatValues.length ? Math.max(...heatValues) : 1;
  function heatColor(value: number) {
    const t = Math.max(0, Math.min(1, (value - heatMin) / (heatMax - heatMin || 1)));
    // Observed original green-to-blue magnitude encoding; exact Tableau scale bounds are not saved.
    const start = [218, 237, 220],
      end = [78, 137, 178];
    return `rgb(${start.map((v, i) => Math.round(v + (end[i] - v) * t)).join(',')})`;
  }
  const filteredRows = search
    ? rows.filter((row) =>
        panel.columns.some((column) =>
          String(row[column.key] ?? 'Unknown')
            .toLowerCase()
            .includes(search.toLowerCase()),
        ),
      )
    : rows;
  const paginated = panel.kind !== 'heatmap';
  const pageSize = 50;
  const pages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, pages - 1);
  const visibleRows = paginated ? filteredRows.slice(currentPage * pageSize, (currentPage + 1) * pageSize) : filteredRows;
  return (
    <>
      {printing && previewRows && filteredRows.length > visibleRows.length && (
        <p>
          Table preview: {visibleRows.length} of {filteredRows.length.toLocaleString('en-AU')} rows. Open the report for full
          detail.
        </p>
      )}
      {paginated && !printing && (
        <div className="lp-table-tools">
          <label className="lp-table-search">
            <span>Search rows</span>
            <ReportInput
              aria-label={`Search ${panel.title}`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <span>
            {filteredRows.length ? currentPage * 50 + 1 : 0}–{Math.min((currentPage + 1) * 50, filteredRows.length)} of{' '}
            {filteredRows.length.toLocaleString('en-AU')}
          </span>
          <ReportButton
            disabled={currentPage === 0}
            onClick={() => setPage((page) => Math.max(0, page - 1))}
            aria-label={`Previous ${panel.title} page`}
          >
            Previous
          </ReportButton>
          <ReportButton
            disabled={currentPage === pages - 1}
            onClick={() => setPage((page) => Math.min(pages - 1, page + 1))}
            aria-label={`Next ${panel.title} page`}
          >
            Next
          </ReportButton>
        </div>
      )}
      <div
        className={`lp-table-scroll ${panel.kind === 'heatmap' ? 'lp-heatmap' : ''}`}
        tabIndex={0}
        role="region"
        aria-label={`${panel.title} rows`}
      >
        <table aria-label={panel.title}>
          <thead>
            <tr>
              {panel.columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={c.format ? 'lp-numeric' : ''}
                  aria-sort={sort.key === c.key ? (sort.direction === 1 ? 'ascending' : 'descending') : 'none'}
                >
                  <ReportButton
                    type="button"
                    onClick={() =>
                      setSort((old) => ({ key: c.key, direction: old.key === c.key ? -old.direction : 1 }))
                    }
                  >
                    {c.label}
                    {sort.key === c.key ? (sort.direction === 1 ? ' ↑' : ' ↓') : ''}
                  </ReportButton>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length ? (
              visibleRows.map((row, i) => (
                <tr key={i}>
                  {panel.columns.map((c) => {
                    const value = row[c.key];
                    const heat = panel.kind === 'heatmap' && typeof value === 'number';
                    return (
                      <td
                        key={c.key}
                        className={`${c.format ? 'lp-numeric' : ''} ${value === null ? 'lp-unknown-cell' : ''}`}
                        style={heat ? { background: heatColor(value), color: '#111111' } : undefined}
                      >
                        {!printing && onSourceMark && panel.id.startsWith('source-') && !c.format ? (
                          <>
                            <ReportButton onClick={() => onSourceMark(row, [c.key])}>{formatted(value)}</ReportButton>
                            {sourceRecordLink(c.key, value) && (
                              <a href={sourceRecordLink(c.key, value)} target="_blank" rel="noopener noreferrer">
                                Open record
                              </a>
                            )}
                          </>
                        ) : !printing && c.key === 'market' && typeof value === 'string' && value !== 'Unknown' ? (
                          <ReportButton
                            className="lp-market-link"
                            onClick={() => onMarket(value)}
                            title={`Filter dashboard to ${value}`}
                          >
                            {value}
                          </ReportButton>
                        ) : (
                          formatted(
                            value,
                            c.format,
                            panel.kind === 'heatmap'
                              ? panel.id === 'ordersCumulative'
                                ? 3
                                : panel.id === 'sessionsCumulative'
                                  ? 2
                                  : panel.id === 'marginCumulative'
                                    ? 1
                                    : 0
                              : undefined,
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={panel.columns.length}>
                  {search
                    ? 'No matching rows. Clear the search to see the filtered dataset.'
                    : 'Unknown — no verified rows for this selection.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PanelChart({
  panel,
  cadence,
  onDate,
  printing,
}: {
  panel: FidelityPanel;
  cadence: FidelityCadence;
  onDate: (date: string) => void;
  printing: boolean;
}) {
  const defaultHidden = () =>
    panel.id === 'takeup-rate' && panel.series.length > 1
      ? panel.series
          .filter(
            (series) =>
              ![
                'Australia',
                'Canada',
                'Germany',
                'United Kingdom',
                'Hong Kong',
                'Malaysia',
                'New Zealand',
                'Singapore',
                'United States',
              ].includes(series.label),
          )
          .map((series) => series.key)
      : [];
  const [hiddenSeries, setHiddenSeries] = useState<Array<string>>(defaultHidden);
  useEffect(
    () => setHiddenSeries(defaultHidden()),
    [panel.id, panel.series.map((series) => `${series.key}:${series.label}`).join('|')],
  );
  const hasData = panel.rows.some((r) => panel.series.some((s) => typeof r[s.key] === 'number'));
  const key = panel.rows.some((r) => r.date) ? 'date' : 'label';
  if (!hasData) {
    return (
      <div className="lp-empty-chart">
        <strong>Unknown</strong>
        <span>No verified series for this selection.</span>
      </div>
    );
  }
  const left = panel.series.find((s) => s.axis !== 'right');
  const right = panel.series.find((s) => s.axis === 'right');
  const horizontal = panel.horizontal;
  const percent = panel.stackMode === 'percent';
  function selectDate(event: unknown) {
    const state = event as { activePayload?: Array<{ payload?: FidelityRow }>; activeLabel?: string } | null;
    const value = state?.activePayload?.[0]?.payload?.date ?? state?.activeLabel;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      onDate(value);
    }
  }
  return (
    <>
      <div className="lp-chart-legend" aria-label={`${panel.title} legend`}>
        {panel.series.map((s) => (
          <ReportButton
            key={s.key}
            className="lp-legend-series"
            aria-pressed={!hiddenSeries.includes(s.key)}
            onClick={() =>
              setHiddenSeries((old) => (old.includes(s.key) ? old.filter((key) => key !== s.key) : [...old, s.key]))
            }
          >
            <i style={{ borderTopColor: s.color, borderTopStyle: s.dashed ? 'dashed' : 'solid' }} />
            {s.label}
          </ReportButton>
        ))}
        {hiddenSeries.length > 0 && <ReportButton onClick={() => setHiddenSeries([])}>Show all series</ReportButton>}
      </div>
      {panel.id === 'takeup-rate' && panel.series.length > 1 && (
        <p className="lp-panel-note">
          Plot display: {panel.series.length - hiddenSeries.length} of {panel.series.length} country series. Starts with
          the workbook's saved nine countries; toggle the legend to change the display. Tables and counts retain the
          global country scope.
        </p>
      )}
      <div
        className="lp-chart"
        style={{
          height: horizontal ? Math.max(panel.height ?? 320, panel.rows.length * 26 + 42) : (panel.height ?? 320),
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={panel.rows}
            layout={horizontal ? 'vertical' : 'horizontal'}
            stackOffset={percent ? 'expand' : 'none'}
            margin={{ top: 8, right: right ? 8 : 16, bottom: horizontal ? 5 : 24, left: 0 }}
            onClick={selectDate}
            accessibilityLayer
          >
            <CartesianGrid vertical={false} stroke="#e6e6e6" />
            <XAxis
              dataKey={horizontal ? undefined : key}
              type={horizontal ? 'number' : 'category'}
              tickCount={horizontal ? 4 : undefined}
              tickFormatter={horizontal ? (v) => compact(v, percent ? 'percent' : left?.format) : shortDate}
              tick={{ fill: '#555', fontSize: 11 }}
              minTickGap={28}
              tickLine={false}
              axisLine={{ stroke: '#b8b8b8' }}
              label={
                horizontal
                  ? undefined
                  : {
                      value:
                        key === 'date'
                          ? cadence === 'week'
                            ? 'Week commencing (Monday)'
                            : cadence === 'month'
                              ? 'Month'
                              : 'Date'
                          : 'Group',
                      position: 'insideBottom',
                      offset: -16,
                      fontSize: 11,
                      fill: '#555',
                    }
              }
            />
            <YAxis
              yAxisId="left"
              dataKey={horizontal ? key : undefined}
              type={horizontal ? 'category' : 'number'}
              width={horizontal ? 138 : 62}
              tick={{ fill: '#555', fontSize: 11 }}
              tickFormatter={
                horizontal
                  ? (v) => (String(v).length > 24 ? `${String(v).slice(0, 23)}…` : String(v))
                  : (v) => compact(v, percent ? 'percent' : left?.format)
              }
              tickLine={false}
              axisLine={false}
            />
            {right && (
              <YAxis
                yAxisId="right"
                orientation="right"
                width={44}
                tick={{ fill: '#555', fontSize: 11 }}
                tickFormatter={(v) => compact(v, right.format)}
                tickLine={false}
                axisLine={false}
              />
            )}
            <ReportChartTooltip
              labelFormatter={shortDate}
              formatter={(v: unknown, name: unknown) => [
                formatted(v, panel.series.find((s) => s.label === name)?.format),
                String(name),
              ]}
              contentStyle={{ background: '#fff', border: '1px solid #aaa', color: '#363a45', fontSize: 11 }}
            />
            {panel.series.map((s) =>
              s.type === 'bar' || (panel.kind === 'bar' && s.type !== 'line') ? (
                <Bar
                  key={s.key}
                  hide={hiddenSeries.includes(s.key)}
                  dataKey={s.key}
                  name={s.label}
                  fill={s.color}
                  yAxisId={s.axis ?? 'left'}
                  stackId={panel.stacked ? 'total' : undefined}
                  maxBarSize={30}
                  isAnimationActive={false}
                />
              ) : (
                <Line
                  key={s.key}
                  hide={hiddenSeries.includes(s.key)}
                  type="linear"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  yAxisId={s.axis ?? 'left'}
                  strokeWidth={2.3}
                  strokeDasharray={s.dashed ? '5 3' : undefined}
                  dot={panel.rows.length < 3 ? { r: 3 } : false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="lp-chart-action">
        {percent
          ? 'Share of orders (%)'
          : left?.format === 'money'
            ? 'AUD'
            : left?.format === 'percent'
              ? 'Rate (%)'
              : panel.valueFormat === 'decimal'
                ? 'Average per customer'
                : 'Count'}
        {key === 'date' ? ' · Select a plotted date to filter this dashboard.' : ''}
      </p>
    </>
  );
}

export default function LuxPlusWorkbook({
  board,
  printing = false,
  previewRows = false,
  reportActions,
  model,
  filterOptions,
  ready,
  problem,
  loading = false,
  loadedCards = 0,
  totalCards = 0,
  streamedMetrics = [],
  cadence,
  onCadence: setCadence,
  split,
  onSplit: setSplit,
  exportPanel,
  extraControls,
  onSourceMark,
  queriedAt,
  filters,
  view,
  onChange,
  theme,
  layout,
  controls = htmlControls,
  nativeControls = false,
  nativeFlow = false,
}: LuxPlusWorkbookProps) {
  const [showKpis, setShowKpis] = useState(true);
  const [showCharts, setShowCharts] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [info, setInfo] = useState(false);
  const [dateDraft, setDateDraft] = useState({ from: filters.startDate, to: filters.endDate });
  const activeOptions = filterOptions.filter(
    (option) =>
      (filters[option.key] !== undefined && filters[option.key] !== option.defaultValue) ||
      (option.defaultValue !== 'All' && option.options.includes('All')),
  );
  useEffect(() => {
    setDateDraft({ from: filters.startDate, to: filters.endDate });
  }, [filters.startDate, filters.endDate]);
  const valid = (v: string) =>
    !v ||
    (/^\d{4}-\d{2}-\d{2}$/.test(v) &&
      Number.isFinite(Date.parse(`${v}T00:00:00Z`)) &&
      new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v);
  const invalid =
    !valid(filters.startDate) ||
    !valid(filters.endDate) ||
    (!!filters.startDate && !!filters.endDate && filters.startDate > filters.endDate);
  function applyDates(event: React.FormEvent) {
    event.preventDefault();
    if (
      valid(dateDraft.from) &&
      valid(dateDraft.to) &&
      (!dateDraft.from || !dateDraft.to || dateDraft.from <= dateDraft.to)
    ) {
      setDateError(false);
      onChange({ startDate: dateDraft.from, endDate: dateDraft.to });
    } else {
      setDateError(true);
    }
  }
  function selectDate(date: string) {
    const d = new Date(`${date}T00:00:00Z`);
    if (cadence === 'week') {
      d.setUTCDate(d.getUTCDate() + 6);
    }
    if (cadence === 'month') {
      d.setUTCMonth(d.getUTCMonth() + 1, 0);
    }
    const from =
      date < (filters.startDate || model.range.min || date) ? filters.startDate || model.range.min || date : date;
    const end = d.toISOString().slice(0, 10);
    const to = end > (filters.endDate || model.range.max || end) ? filters.endDate || model.range.max || end : end;
    onChange({ startDate: from, endDate: to });
  }
  const cohortMonths =
    filterOptions.find((option) => option.key === 'enrollmentMonth')?.options.filter((value) => value !== 'All') ?? [];
  const selectedPeriod =
    view === 'cohorts'
      ? `Enrollment ${filters.enrollmentMonth && filters.enrollmentMonth !== 'All' ? filters.enrollmentMonth : `${cohortMonths[0]?.slice(0, 7) || 'Unknown'} — ${cohortMonths[cohortMonths.length - 1]?.slice(0, 7) || 'Unknown'}`}`
      : `${filters.startDate || model.range.min || 'Unknown'} — ${filters.endDate || model.range.max || 'Unknown'}`;
  const exportable =
    board.panels.find((p) => p.kind === 'table' && p.rows.length) ?? board.panels.find((p) => p.rows.length);
  function resetFilters() {
    const cleared = Object.fromEntries(Object.keys(filters).map((key) => [key, undefined]));
    onChange({ ...cleared, market: 'All markets', startDate: '', endDate: '', ...getFidelityDefaultFilters(view) });
    setShowKpis(true);
    setShowCharts(true);
    setDateError(false);
  }
  const chosenControls = nativeControls || layout === 'workbook' ? controls : htmlControls;
  const InfoDialog = chosenControls.Dialog;
  return (
    <LuxPlusControlContext.Provider value={chosenControls}>
      <div
        className={`lp-workbook lp-${layout}${nativeFlow ? ' lp-native-flow' : ''}`}
        data-lp-theme={theme}
        data-lp-print={printing}
        data-lp-view={view}
      >
        <header className="lp-workbook-header">
          <div>
            <div className="lp-report-identity">
              <span className="lp-wordmark">LUXPLUS+</span>
              <h1>{board.title.replace(/^LP\+ /, '')}</h1>
            </div>
            {board.subtitle ? <p>{board.subtitle}</p> : null}
          </div>
          <div className="lp-tools" data-lens-pdf-ignore>
            <span className="lp-action-menu">{reportActions}</span>
            <PageChatButton />
            <ReportButton
              className="lp-tool-button"
              onClick={() => exportable && exportPanel(exportable)}
              disabled={!ready || !exportable}
            >
              Export CSV
            </ReportButton>
          </div>
        </header>
        <nav data-lens-pdf-ignore className="lp-view-tabs" aria-label="LuxPlus dashboards">
          {FIDELITY_VIEWS.map((v) => (
            <ReportButton
              key={v.id}
              aria-current={v.id === view ? 'page' : undefined}
              onClick={() => onChange({ view: v.id })}
            >
              {v.label.replace(/^LP\+ /, '')}
            </ReportButton>
          ))}
        </nav>
        <div className="lp-workbook-scroll">
          <div className="lp-board-and-filters">
            <main className="lp-board" aria-label={board.title}>
              <div className="lp-period">
                <span>
                  {selectedPeriod} · {filters.market}
                </span>
                {queriedAt && <span>{queriedAt}</span>}
              </div>
              <div data-lens-pdf-ignore className="lp-active-filters" aria-label="Active filters">
                <span>Active filters</span>
                {filters.market !== 'All markets' && (
                  <ReportButton onClick={() => onChange({ market: 'All markets' })}>
                    Country: {filters.market} ×
                  </ReportButton>
                )}
                {(filters.startDate || filters.endDate) && (
                  <ReportButton onClick={() => onChange({ startDate: '', endDate: '' })}>
                    Date: {selectedPeriod} ×
                  </ReportButton>
                )}
                {activeOptions.map((option) => (
                  <ReportButton
                    key={option.key}
                    onClick={() =>
                      onChange({
                        [option.key]:
                          (filters[option.key] ?? option.defaultValue) === option.defaultValue &&
                          option.options.includes('All')
                            ? 'All'
                            : option.defaultValue,
                      })
                    }
                  >
                    {option.label}: {String(filters[option.key] ?? option.defaultValue)} ×
                  </ReportButton>
                ))}
                {!activeOptions.length &&
                  filters.market === 'All markets' &&
                  !filters.startDate &&
                  !filters.endDate && <span>Default scope</span>}
                <ReportButton onClick={resetFilters}>Reset all</ReportButton>
              </div>
              {view === 'overview' && (filters.market !== 'All markets' || filters.startDate || filters.endDate) && (
                <p className="lp-scope-note">
                  Margin charts and the country summary use this selection. Membership snapshots and fixed All time /
                  Yesterday counts retain their labelled windows and all-country scope.
                </p>
              )}
              {loading && (
                <div className="lp-loading" role="status" aria-live="polite">
                  <div className="lp-loading-head">
                    <strong>Loading live results</strong>
                    <span>
                      {loadedCards} of {totalCards} sources ready
                    </span>
                  </div>
                  <div
                    className="lp-loading-track"
                    role="progressbar"
                    aria-valuenow={loadedCards}
                    aria-valuemin={0}
                    aria-valuemax={totalCards}
                  >
                    <span style={{ width: `${totalCards ? (loadedCards / totalCards) * 100 : 0}%` }} />
                  </div>
                  <p>
                    Margin totals appear as soon as their source returns. The charts and other measures follow when
                    verified.
                  </p>
                </div>
              )}
              {problem && !loading && (
                <p role="alert" className="lp-alert">
                  {problem}
                </p>
              )}
              {invalid && view !== 'cohorts' ? (
                <p role="alert" className="lp-alert">
                  Select valid dates with From on or before To. Results are Unknown.
                </p>
              ) : null}
              <div className="lp-panel-grid">
                {loading && streamedMetrics.length > 0 && (
                  <section className="lp-panel lp-streamed-headlines lp-span-full" aria-label="Loaded margin totals">
                    <div className="lp-panel-heading">
                      <h2>Margin · selected period</h2>
                      <span className="lp-scope-tag">Live result</span>
                    </div>
                    <dl className="lp-kpis">
                      {streamedMetrics.map((metric) => (
                        <div key={metric.label}>
                          <dt>{metric.label}</dt>
                          <dd>{formatted(metric.value, 'money')}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                )}
                {loading && board.panels.length === 0 && view === 'overview' && (
                  <>
                    {streamedMetrics.length === 0 && (
                      <section className="lp-panel lp-loading-placeholder lp-span-full" aria-hidden="true">
                        <div className="lp-placeholder-title" />
                        <div className="lp-placeholder-row">
                          {[0, 1, 2, 3, 4].map((index) => (
                            <i key={index} />
                          ))}
                        </div>
                      </section>
                    )}
                    <section className="lp-panel lp-loading-placeholder lp-span-full" aria-hidden="true">
                      <div className="lp-placeholder-title" />
                      <div className="lp-placeholder-row">
                        {[0, 1, 2, 3].map((index) => (
                          <i key={index} />
                        ))}
                      </div>
                    </section>
                  </>
                )}
                {board.panels
                  .filter(
                    (p) => (showKpis || p.kind !== 'metrics') && (showCharts || ['metrics', 'table'].includes(p.kind)),
                  )
                  .map((panel) => (
                    <React.Fragment key={panel.id}>
                      <section
                        data-lp-panel={panel.id}
                        data-lens-pdf-card
                        data-lens-pdf-card-type={
                          panel.kind === 'table' || panel.kind === 'heatmap' ? 'table' : undefined
                        }
                        className={`lp-panel lp-span-${panel.span ?? 'full'} lp-kind-${panel.kind}`}
                        aria-label={panel.title}
                      >
                        <div className="lp-panel-heading">
                          <h2>{panel.title}</h2>
                          {view === 'overview' && panel.kind === 'metrics' && (
                            <span className="lp-scope-tag">{panel.id === 'health' ? 'Blended' : 'All countries'}</span>
                          )}
                          {panel.rows.length > 0 && (
                            <ReportButton
                              aria-label={`Download ${panel.title} as CSV`}
                              disabled={!ready}
                              onClick={() => exportPanel(panel)}
                            >
                              CSV ↓
                            </ReportButton>
                          )}
                        </div>
                        {showKpis &&
                          !!panel.metrics?.length &&
                          (view === 'overview' && panel.id.startsWith('members-') && panel.id !== 'members-today' ? (
                            <OverviewMembershipMetrics panel={panel} format={formatted} />
                          ) : (
                            <dl className="lp-kpis">
                              {panel.metrics.map((m) => (
                                <div key={m.key} title={m.definition}>
                                  <dt>
                                    {panel.id === 'health' && m.key === 'takeup'
                                      ? 'Eligible executive take-up (selected filters)'
                                      : m.label}
                                  </dt>
                                  <dd data-lp-metric={m.key}>{formatted(m.value, m.format)}</dd>
                                </div>
                              ))}
                            </dl>
                          ))}
                        {['line', 'bar', 'composed'].includes(panel.kind) && (
                          <PanelChart panel={panel} cadence={cadence} onDate={selectDate} printing={printing} />
                        )}
                        {['table', 'heatmap'].includes(panel.kind) && (
                          <PanelTable
                            printing={printing}
                            previewRows={previewRows}
                            panel={panel}
                            onMarket={(market) => onChange({ market })}
                            onSourceMark={ready ? onSourceMark : undefined}
                          />
                        )}
                        {panel.kind === 'unknown' && (
                          <div className="lp-empty-chart">
                            <strong>Unknown</strong>
                            <span>{panel.note?.replace(/^Unknown\s*[—–-]\s*/, '')}</span>
                          </div>
                        )}
                        {panel.note && panel.kind !== 'unknown' && <p className="lp-panel-note">{panel.note}</p>}
                      </section>
                    </React.Fragment>
                  ))}
              </div>
              <footer data-lens-pdf-ignore className="lp-source-footer">
                Live source · {board.source} · {queriedAt ? queriedAt + ' · ' : ''}
                <ReportButton onClick={() => setInfo(true)}>
                  Source and feature gaps ({board.missingFeatures.length})
                </ReportButton>
              </footer>
            </main>
            <aside data-lens-pdf-ignore className="lp-filter-rail" aria-label="Global filters">
              <details open={filtersOpen} onToggle={(event) => setFiltersOpen(event.currentTarget.open)}>
                <summary>Filters & breakdowns {filtersOpen ? '−' : '+'}</summary>
                <div className="lp-filter-grid">
                  {view !== 'cohorts' && (
                    <form onSubmit={applyDates}>
                      <h2>Transaction dates</h2>
                      <label>
                        From
                        <ReportInput
                          type="date"
                          aria-label="From"
                          value={dateDraft.from || model.range.min || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDateDraft((old) => ({ ...old, from: value }));
                          }}
                          onInput={(e) => {
                            const value = e.currentTarget.value;
                            setDateDraft((old) => ({ ...old, from: value }));
                          }}
                        />
                      </label>
                      <label>
                        To
                        <ReportInput
                          type="date"
                          aria-label="To"
                          value={dateDraft.to || model.range.max || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDateDraft((old) => ({ ...old, to: value }));
                          }}
                          onInput={(e) => {
                            const value = e.currentTarget.value;
                            setDateDraft((old) => ({ ...old, to: value }));
                          }}
                        />
                      </label>
                      <ReportButton type="submit">Apply dates</ReportButton>
                      {dateError && (
                        <p role="alert" className="lp-date-error">
                          From must be a valid date on or before To.
                        </p>
                      )}
                    </form>
                  )}
                  {view === 'cohorts' ? (
                    <p className="lp-filter-note">
                      Enrollment month selection is independent of transaction dates. Month of sign-up excluded. All
                      markets only.
                    </p>
                  ) : (
                    <label>
                      Customer / membership country
                      <ReportSelect
                        aria-label="Market"
                        value={filters.market}
                        onChange={(e) => onChange({ market: e.target.value })}
                      >
                        {!['All markets', ...model.markets].includes(filters.market) && (
                          <option value={filters.market}>{filters.market} (outside returned options)</option>
                        )}
                        {['All markets', ...model.markets].map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </ReportSelect>
                    </label>
                  )}
                  {view !== 'cohorts' && (
                    <label>
                      Chart cadence
                      <ReportSelect
                        aria-label="Chart cadence"
                        value={cadence}
                        onChange={(e) => setCadence(e.target.value as FidelityCadence)}
                      >
                        <option value="day">Daily</option>
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                      </ReportSelect>
                    </label>
                  )}
                  {view === 'takeup-trend' && (
                    <label>
                      Data split
                      <ReportSelect
                        aria-label="Data split"
                        value={split ?? 'all'}
                        onChange={(event) => setSplit(event.target.value === 'market' ? 'market' : undefined)}
                      >
                        <option value="market">Country</option>
                        <option value="all">All selected orders</option>
                      </ReportSelect>
                    </label>
                  )}
                  {filterOptions.map((option) => (
                    <DimensionFilter
                      key={option.key}
                      option={option}
                      value={String(filters[option.key] ?? option.defaultValue)}
                      onChange={(value) => onChange({ [option.key]: value })}
                    />
                  ))}
                </div>
                {extraControls}
                <div className="lp-display-options">
                  <label className="lp-check">
                    <ReportInput type="checkbox" checked={showKpis} onChange={(e) => setShowKpis(e.target.checked)} />
                    Show KPIs
                  </label>
                  <label className="lp-check">
                    <ReportInput
                      type="checkbox"
                      checked={showCharts}
                      onChange={(e) => setShowCharts(e.target.checked)}
                    />
                    Show charts
                  </label>
                </div>
              </details>
            </aside>
          </div>
        </div>
        <InfoDialog className="lp-info-dialog" open={info} onClose={() => setInfo(false)}>
          <header>
            <h2 id="lp-info-title">LuxPlus · source and feature parity</h2>
            <ReportButton onClick={() => setInfo(false)}>Close</ReportButton>
          </header>
          <p>
            Live governed source cards feed the preserved workbook presentation. {queriedAt ? queriedAt + '.' : ''} The
            source tables&apos; own refresh time is not shown. Independent numeric comparisons remain pending.
          </p>
          <h3>Current view</h3>
          <p>
            {board.title} · {selectedPeriod}. {board.source}
          </p>
          <h3>Source notes</h3>
          <ul>
            {board.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          <h3>Missing features / figures to verify</h3>
          <ul>
            {board.missingFeatures.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          <p>
            Original chart colours and workflows guide this reconstruction. Cohorts use the observed green-to-blue
            magnitude encoding; exact Tableau scale stops and bounds remain unverified. Current and TTLY rows share one
            scale per metric. Numbers identify meaning independently of colour. The live workbook uses different date
            windows and later refreshes, so its unfiltered totals are not snapshot validation controls.
          </p>
          <h3>Original worksheets</h3>
          <p>{board.originalSheets.join(' · ')}</p>
        </InfoDialog>
      </div>
    </LuxPlusControlContext.Provider>
  );
}
